/**
 * Secure client-seed generation for Provably Fair.
 *
 * Algorithm:
 * 1. Collect playerId, gameId/roundId, Date.now(), 32 CSPRNG bytes
 * 2. Build input: `${playerId}|${gameId}|${timestamp}|${hex(random)}`
 * 3. SHA-256(input)
 * 4. Base62-encode the digest; return the first 20 characters
 */

const BASE62 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const SEED_LENGTH = 20;
const RANDOM_BYTE_COUNT = 32;

export type ClientSeedInput = {
  playerId: string;
  gameId: string;
};

/** Cryptographically secure random bytes (Web Crypto). */
function getSecureRandomBytes(length: number): Uint8Array {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
    throw new Error(
      '[ClientSeed] crypto.getRandomValues is not available in this environment'
    );
  }
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

/**
 * Minimal SHA-256 (sync) over UTF-8 text.
 * Uses Web Crypto when a synchronous path is unavailable — browsers only
 * expose async subtle.digest, so we ship a compact pure implementation.
 */
function sha256Bytes(message: string): Uint8Array {
  const msg = new TextEncoder().encode(message);
  const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);

  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

  const bitLen = msg.length * 8;
  const withPad = new Uint8Array((msg.length + 9 + 63) & ~63);
  withPad.set(msg);
  withPad[msg.length] = 0x80;
  const view = new DataView(withPad.buffer);
  // length in bits as 64-bit big-endian (high 32 always 0 for our sizes)
  view.setUint32(withPad.length - 4, bitLen >>> 0, false);

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Uint32Array(64);

  for (let i = 0; i < withPad.length; i += 64) {
    for (let j = 0; j < 16; j++) {
      w[j] = view.getUint32(i + j * 4, false);
    }
    for (let j = 16; j < 64; j++) {
      const s0 = rotr(w[j - 15], 7) ^ rotr(w[j - 15], 18) ^ (w[j - 15] >>> 3);
      const s1 = rotr(w[j - 2], 17) ^ rotr(w[j - 2], 19) ^ (w[j - 2] >>> 10);
      w[j] = (w[j - 16] + s0 + w[j - 7] + s1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let j = 0; j < 64; j++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + K[j] + w[j]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  const digest = new Uint8Array(32);
  const out = new DataView(digest.buffer);
  out.setUint32(0, h0, false);
  out.setUint32(4, h1, false);
  out.setUint32(8, h2, false);
  out.setUint32(12, h3, false);
  out.setUint32(16, h4, false);
  out.setUint32(20, h5, false);
  out.setUint32(24, h6, false);
  out.setUint32(28, h7, false);
  return digest;
}

/** @internal Exported for verification against known SHA-256 vectors. */
export function sha256Hex(message: string): string {
  return bytesToHex(sha256Bytes(message));
}

/** Encode digest bytes as Base62 (A-Z, a-z, 0-9). */
function bytesToBase62(bytes: Uint8Array): string {
  let value = 0n;
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8n) + BigInt(bytes[i]);
  }
  if (value === 0n) {
    return BASE62[0];
  }
  let encoded = '';
  while (value > 0n) {
    encoded = BASE62[Number(value % 62n)] + encoded;
    value /= 62n;
  }
  return encoded;
}

/**
 * Generate a 20-character Base62 client seed.
 * Same playerId+gameId still yields a different seed each call (timestamp + CSPRNG).
 */
export function generateSecureClientSeed(
  playerId: string,
  gameId: string
): string {
  const timestamp = Date.now();
  const randomBytes = getSecureRandomBytes(RANDOM_BYTE_COUNT);
  const input = `${playerId}|${gameId}|${timestamp}|${bytesToHex(randomBytes)}`;
  const digest = sha256Bytes(input);
  const base62 = bytesToBase62(digest);
  const seed = base62.slice(0, SEED_LENGTH);

  if (seed.length !== SEED_LENGTH || !/^[A-Za-z0-9]{20}$/.test(seed)) {
    // Extremely unlikely for SHA-256; pad from leftover entropy if needed
    const pad = bytesToBase62(getSecureRandomBytes(32));
    return (seed + pad).slice(0, SEED_LENGTH);
  }
  return seed;
}

/**
 * Singleton holding the active client seed for JOIN / PLACE_BET / PF settings UI.
 * Rotates when the player+round context changes so each game/round gets a unique seed.
 */
export class ClientSeedService {
  private static instance: ClientSeedService;
  private clientSeed: string;
  private contextKey: string | null = null;

  private constructor() {
    this.clientSeed = generateSecureClientSeed('pending', 'init');
  }

  public static getInstance(): ClientSeedService {
    if (!ClientSeedService.instance) {
      ClientSeedService.instance = new ClientSeedService();
    }
    return ClientSeedService.instance;
  }

  public getClientSeed(): string {
    return this.clientSeed;
  }

  /**
   * Return the seed for this player + game/round.
   * Regenerates when the context changes (new round / player).
   */
  public ensureClientSeed(playerId: string, gameId: string): string {
    const key = `${playerId}|${gameId}`;
    if (this.contextKey !== key) {
      this.clientSeed = generateSecureClientSeed(playerId, gameId);
      this.contextKey = key;
    }
    return this.clientSeed;
  }

  /** Always mint a new seed for the given context (e.g. explicit refresh). */
  public rotateClientSeed(playerId: string, gameId: string): string {
    this.clientSeed = generateSecureClientSeed(playerId, gameId);
    this.contextKey = `${playerId}|${gameId}`;
    return this.clientSeed;
  }
}
